export const IMAGE_VARIANTS = ["avatar", "avatar2x", "thumb", "card"] as const;
export type ImageVariant = (typeof IMAGE_VARIANTS)[number];

// Must stay in sync with the variant table the images Worker serves (cloudflare/images-worker).
const IMAGE_VARIANT_WIDTHS: Record<ImageVariant, number> = {
  avatar: 64,
  avatar2x: 128,
  thumb: 256,
  card: 512,
};

// String-parsed (no URL()) because React Native's built-in URL is a stub whose accessors throw.
// Matches per-env CDN hosts (img.miter.com / staging / internal) and local Tilt Worker (http://localhost:<port>).
const CDN_URL_PATTERN =
  /^(https:\/\/img\.(?:[a-z0-9-]+\.)?miter\.com|http:\/\/(?:localhost|127\.0\.0\.1):\d+)\/([^/?#]+)(\/[^/?#]+)?(\?[^#]*)?$/;

export const getWebDevicePixelRatio = (): number => {
  return typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
};

/** Smallest Worker variant that still covers the rendered slot at the device's pixel density. */
export const pickImageVariant = (displayPx: number, devicePixelRatio = 1): ImageVariant => {
  const neededPx = displayPx * devicePixelRatio;
  return IMAGE_VARIANTS.find((variant) => IMAGE_VARIANT_WIDTHS[variant] >= neededPx) ?? "card";
};

/**
 * Rewrites an Images CDN URL (img.miter.com/<key>?sig=..) to its sized variant
 * (img.miter.com/<key>/<variant>?sig=..). Anything else (presigned S3 URLs, blob/data URLs,
 * URLs that already carry a variant) passes through untouched, so call sites can apply it
 * unconditionally whether or not the company is ramped onto the CDN.
 */
export const withImageVariant = (url: string, variant: ImageVariant): string => {
  const match = CDN_URL_PATTERN.exec(url);
  if (!match) return url;

  const [, origin, key, existingVariant, query = ""] = match;
  if (existingVariant) return url;

  return `${origin}/${key}/${variant}${query}`;
};
